import { useEffect, useState, useCallback } from "react";
import useIsMobile from "../hooks/useIsMobile";

// Public, unauthenticated, customer-facing system status page.
// Fetches /api/status (cached server-side) and auto-refreshes. Intentionally
// light on detail: a clear "all good on our end" signal plus the third-party
// services our clients depend on (Microsoft 365 / Outlook / Teams, Adobe, Vonage).

const API = (process.env.REACT_APP_API_URL || "") + "/api/status";

const LEVEL = {
  operational: { label: "Operational",  dot: "#22c55e", text: "#34d399", soft: "rgba(34,197,94,0.12)" },
  degraded:    { label: "Degraded",      dot: "#f59e0b", text: "#fbbf24", soft: "rgba(245,158,11,0.12)" },
  down:        { label: "Disruption",    dot: "#ef4444", text: "#f87171", soft: "rgba(239,68,68,0.12)" },
  unknown:     { label: "Unavailable",   dot: "#94a3b8", text: "#94a3b8", soft: "rgba(148,163,184,0.12)" },
};

const OVERALL_COPY = {
  operational: "All systems operational",
  degraded:    "Some systems experiencing issues",
  down:        "Active service disruption",
  unknown:     "Status partially unavailable",
};

// MIDTC's own service is keyed "ticketing"; everything else is a third party.
const OUR_KEYS = new Set(["ticketing"]);

function Dot({ level, size = 10 }) {
  const c = LEVEL[level] || LEVEL.unknown;
  return <span style={{ width: size, height: size, borderRadius: "50%", background: c.dot, flexShrink: 0, display: "inline-block", boxShadow: `0 0 0 3px ${c.soft}` }} />;
}
function Pill({ level }) {
  const c = LEVEL[level] || LEVEL.unknown;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "4px 12px", borderRadius: 99, background: c.soft, color: c.text, fontSize: 12, fontWeight: 700 }}>
      <Dot level={level} size={8} /> {c.label}
    </span>
  );
}

// One service row + optional sub-components (e.g. Outlook / Teams under M365).
// `hideComponents` strips internal infra names for the customer view.
function ServiceRow({ svc, hideInternal }) {
  const comps = (svc.components || []).filter(c => !hideInternal || !/database|pipeline/i.test(c.name));
  return (
    <div style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 14, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "15px 20px", borderBottom: comps.length ? "1px solid var(--t-border-soft)" : "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <Dot level={svc.status} />
          <span style={{ fontSize: 15, fontWeight: 700 }}>{svc.name}</span>
        </div>
        <Pill level={svc.status} />
      </div>
      {comps.length > 0 && (
        <div style={{ padding: "6px 20px 12px" }}>
          {comps.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0", borderBottom: i < comps.length - 1 ? "1px solid var(--t-border-softer)" : "none" }}>
              <span style={{ fontSize: 13, color: "var(--t-text-body)" }}>{c.name}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: (LEVEL[c.status] || LEVEL.unknown).text }}>
                <Dot level={c.status} size={7} /> {(LEVEL[c.status] || LEVEL.unknown).label}
              </span>
            </div>
          ))}
        </div>
      )}
      {svc.url && !OUR_KEYS.has(svc.key) && (
        <div style={{ padding: "0 20px 13px" }}>
          <a href={svc.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#60a5fa", textDecoration: "none", fontWeight: 600 }}>Provider status page ↗</a>
        </div>
      )}
    </div>
  );
}

export default function StatusPage() {
  const isMobile = useIsMobile();
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(API, { headers: { Accept: "application/json" } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setData(j); setError(false); setFetchedAt(new Date());
    } catch { setError(true); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, [load]);

  const services = data?.services || [];
  const ours   = services.filter(s => OUR_KEYS.has(s.key));
  const others = services.filter(s => !OUR_KEYS.has(s.key));
  // Overall headline reflects OUR systems only (customers care if WE are up).
  const ourOverall = ours.length
    ? (ours.some(s => s.status === "down") ? "down" : ours.some(s => s.status === "degraded") ? "degraded" : "operational")
    : (data?.overall || "unknown");
  const oc = LEVEL[ourOverall] || LEVEL.unknown;
  const allGood = ourOverall === "operational";

  const sectionLabel = { fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--t-text-faint)", margin: "26px 2px 12px" };

  return (
    <div style={{ minHeight: "100vh", background: "var(--t-page, #111b2f)", color: "var(--t-text-primary, #f1f5f9)", fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: isMobile ? "24px 14px 48px" : "40px 20px 64px" }}>

        {/* Brand */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: "#f97316" }}>MIDTC</span>
          <span style={{ color: "var(--t-text-faint)", fontSize: 13 }}>|</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: "var(--t-text-muted)" }}>Service Status</span>
        </div>

        {/* Hero */}
        <div style={{ background: oc.soft, border: `1px solid ${oc.dot}44`, borderRadius: 16, padding: isMobile ? "18px 16px" : "26px 26px", marginBottom: 14, display: "flex", alignItems: "center", gap: isMobile ? 12 : 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: oc.dot, boxShadow: `0 0 18px ${oc.dot}99` }}>
            {allGood
              ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>
              : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M12 8v5M12 16.5v.5" strokeLinecap="round"/></svg>}
          </div>
          <div>
            <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: oc.text }}>{loading ? "Checking systems…" : OVERALL_COPY[ourOverall]}</div>
            <div style={{ fontSize: 13, color: "var(--t-text-faint)", marginTop: 3 }}>
              {allGood ? "MIDTC services are running normally." : "We're aware and working on it."}
              {fetchedAt && ` · Updated ${fetchedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", padding: "4px 4px 0", marginBottom: 6 }}>
          {["operational", "degraded", "down"].map(l => (
            <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--t-text-muted)" }}>
              <Dot level={l} size={8} /> {LEVEL[l].label}
            </span>
          ))}
        </div>

        {error && !data && (
          <div style={{ textAlign: "center", color: "var(--t-text-faint)", padding: 40, fontSize: 13 }}>Unable to load status right now. Retrying automatically…</div>
        )}

        {/* Our services */}
        {ours.length > 0 && <>
          <div style={sectionLabel}>MIDTC Services</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {ours.map(svc => <ServiceRow key={svc.key} svc={svc} hideInternal />)}
          </div>
        </>}

        {/* Third-party services clients rely on */}
        {others.length > 0 && <>
          <div style={sectionLabel}>Third-Party Services</div>
          <div style={{ fontSize: 12.5, color: "var(--t-text-faint)", margin: "-6px 2px 12px", lineHeight: 1.5 }}>
            Services provided by outside vendors (Microsoft, Adobe, Vonage). Issues here are on the provider's side, not MIDTC's.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {others.map(svc => <ServiceRow key={svc.key} svc={svc} />)}
          </div>
        </>}

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: 36, fontSize: 12, color: "var(--t-text-faint)" }}>
          Need help? Email <a href="mailto:tickets@example.com" style={{ color: "#60a5fa", textDecoration: "none" }}>tickets@example.com</a> · <a href="https://support.example.com" style={{ color: "#60a5fa", textDecoration: "none" }}>Support Portal</a>
        </div>
      </div>
    </div>
  );
}
